import { CheckCircle, Clock, Heart, MonitorPlay } from '@/core/ui/icons';
import { Card, cn, EmptyState, ErrorState, Skeleton } from '@/core/ui';
import { useUserReport } from '../application/audience';
import { watchedLabel } from '../domain/engagement';

const dateTime = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Tudo o que uma pessoa assistiu, aula a aula. */
export function UserReportView({ userId }: { userId: string }) {
  const report = useUserReport(userId);

  if (report.isPending) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-72 rounded-2xl" />
      </div>
    );
  }
  if (report.isError) return <ErrorState error={report.error} onRetry={() => void report.refetch()} />;

  const r = report.data;
  const watched = r.lessons.reduce((n, l) => n + l.watched_s, 0);
  const completed = r.lessons.filter((l) => l.completed).length;
  const liked = r.lessons.filter((l) => l.liked).length;

  return (
    <div className="space-y-4">
      <Card className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-semibold">{r.full_name || r.email}</h2>
          <p className="truncate text-sm text-fg-subtle">
            {[r.email, r.cargo, r.area].filter(Boolean).join(' · ')}
          </p>
        </div>
        <dl className="flex gap-6 text-xs">
          <Stat icon={MonitorPlay} label="Aulas" value={r.lessons.length} />
          <Stat icon={CheckCircle} label="Concluídas" value={completed} />
          <Stat icon={Clock} label="Assistido" value={watchedLabel(watched)} />
          <Stat icon={Heart} label="Curtidas" value={liked} />
        </dl>
      </Card>

      {r.lessons.length === 0 ? (
        <EmptyState title="Esta pessoa ainda não assistiu nenhuma aula" />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[44rem] text-sm">
            <thead className="bg-gray-50/60 text-left text-xs font-medium tracking-wide text-gray-500">
              <tr className="border-b border-line">
                <th className="px-4 py-2.5 font-medium">Aula</th>
                <th className="px-4 py-2.5 font-medium">Progresso</th>
                <th className="px-4 py-2.5 text-right font-medium">Assistido</th>
                <th className="px-4 py-2.5 text-right font-medium">Sessões</th>
                <th className="px-4 py-2.5 font-medium">Último acesso</th>
              </tr>
            </thead>
            <tbody>
              {r.lessons.map((l) => (
                <tr key={l.lesson_id} className="border-b border-line last:border-0">
                  <td className="px-4 py-2.5">
                    <p className="flex items-center gap-1.5 font-medium">
                      {l.lesson_title}
                      {l.liked && (
                        <Heart aria-label="Curtiu" weight="fill" className="size-3 text-brand-400" />
                      )}
                    </p>
                    <p className="text-xs text-fg-subtle">{l.course_title}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="h-1.5 w-24 overflow-hidden rounded-full bg-surface-2">
                        <span
                          className={cn('block h-full', l.completed ? 'bg-brand-400' : 'bg-brand-400')}
                          style={{ width: `${Math.min(100, l.percent)}%` }}
                        />
                      </span>
                      <span className="text-xs text-fg-muted tabular-nums">
                        {l.completed ? 'Concluiu' : `${l.percent}%`}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg-muted tabular-nums">
                    {watchedLabel(l.watched_s)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg-muted tabular-nums">{l.sessions}</td>
                  <td className="px-4 py-2.5 text-fg-muted tabular-nums">
                    {dateTime.format(new Date(l.last_seen_at))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <p className="text-xs text-fg-subtle">Gerado em {dateTime.format(new Date(r.generated_at))}.</p>
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Clock; label: string; value: string | number }) {
  return (
    <div>
      <dt className="flex items-center gap-1 text-fg-subtle">
        <Icon aria-hidden className="size-3.5" />
        {label}
      </dt>
      <dd className="mt-0.5 text-base font-semibold tabular-nums">{value}</dd>
    </div>
  );
}
